import {
    takeEvery, all, put, call, take, fork,
} from 'redux-saga/effects';
import { eventChannel, END } from 'redux-saga';
import API from '../common/API';
import * as Actions from '../actions/ProductManagement';
import { waitAuthorizedCheck } from './Auth';

function createUploaderChannel(payload, token) {
    let emit;
    const chan = eventChannel((emitter) => {
        emit = emitter;
        // it's necessarily. event channel should
        // return unsubscribe function. In our case
        // it's empty function
        return () => {};
    });

    const onProgress = (progressEvent) => {
        const percentCompleted = Math.round((progressEvent.loaded * 100) / progressEvent.total);
        emit(percentCompleted);
        if (percentCompleted === 100) emit(END); // 100 percent emit end
        else emit(percentCompleted);
    };

    const uploadPromise = API.postWithFile({
        endpoint: `/api/v1/company/${payload.companyID}/product/`,
        body: payload.params,
        token,
        onProgress,
    });

    return [uploadPromise, chan];
}

function* getProductsByCompany(action) {
    try {
        const credential = yield call(waitAuthorizedCheck);
        const response = yield call(
            API.get,
            {
                endpoint: `/api/v1/company/${action.payload.companyID}/product/`,
                body: action.payload.params,
                token: credential.data.token,
            },
        );
        yield put(Actions.getProductsByCompanyReceived(response));
    } catch (error) {
        yield put(Actions.getProductsByCompanyReceiveFailure(yield error));
    }
}

function* getProduct(action) {
    try {
        const credential = yield call(waitAuthorizedCheck);
        const response = yield call(
            API.get,
            {
                endpoint: `/api/v1/company/${action.payload.companyID}/product/${action.payload.productID}/`,
                token: credential.data.token,
            },
        );
        yield put(Actions.getProductInfoReceived(response));
    } catch (error) {
        yield put(Actions.getProductInfoReceiveFailure(yield error));
    }
}

function* uploadProgressWatcher(channel) {
    while (true) {
        const progress = yield take(channel);
        yield put(Actions.createProductProgess(progress));
    }
}

function* createProduct(action) {
    try {
        const credential = yield call(waitAuthorizedCheck);
        const [uploadPromise, channel] = createUploaderChannel(action.payload, credential.data.token);
        yield fork(uploadProgressWatcher, channel);
        const response = yield call(() => uploadPromise);
        yield put(Actions.createProductReceived(response));
    } catch (error) {
        yield put(Actions.createProductReceiveFailure(yield error));
    }
}

function* updateProduct(action) {
    try {
        const credential = yield call(waitAuthorizedCheck);
        const response = yield call(
            API.putWithFile,
            {
                endpoint: `/api/v1/company/${action.payload.companyID}/product/${action.payload.productID}/`,
                body: action.payload.params,
                token: credential.data.token,
            },
        );
        yield put(Actions.updateProductReceived(response));
    } catch (error) {
        yield put(Actions.updateProductReceiveFailure(yield error));
    }
}

function* deleteProduct(action) {
    try {
        const credential = yield call(waitAuthorizedCheck);
        const response = yield call(
            API.delete,
            {
                endpoint: `/api/v1/company/${action.payload.companyID}/product/${action.payload.productID}/`,
                token: credential.data.token,
            },
        );
        yield put(Actions.deleteProductReceived(response));
    } catch (error) {
        yield put(Actions.deleteProductReceiveFailure(yield error));
    }
}

function* updateProductOrder(action) {
    try {
        const credential = yield call(waitAuthorizedCheck);
        const response = yield call(
            API.put,
            {
                endpoint: `/api/v1/product/${action.payload.companyID}/sequence/`,
                body: action.payload.objSeq,
                token: credential.data.token,
            },
        );
        yield put(Actions.updateProductOrderReceived(response));
    } catch (error) {
        yield put(Actions.updateProductOrderReceiveFailure(yield error));
    }
}

export default function* login() {
    yield all([
        takeEvery('GET_PRODUCTS_BY_COMPANY_REQUEST', getProductsByCompany),
        takeEvery('GET_PRODUCT_INFO_REQUEST', getProduct),
        takeEvery('CREATE_PRODUCT_REQUEST', createProduct),
        takeEvery('UPDATE_PRODUCT_REQUEST', updateProduct),
        takeEvery('DELETE_PRODUCT_REQUEST', deleteProduct),
        takeEvery('UPDATE_PRODUCT_ORDER_REQUESTED', updateProductOrder),
    ]);
}
